import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MoreHorizontal, Send, XCircle, UserX, UserCheck, Trash2, Loader2, Lock, Shield } from "lucide-react";
import { ROLE_LABEL } from "@/lib/helpers";

const ROLES = ["chauffeur", "regulation", "admin", "administratif"];
const ROLE_HINT = { administratif: "Mes heures (temps réel 100 %) et Congés & demandes uniquement — non roulant, aucun autre module", chauffeur: "Documents à récupérer, rendez-vous, points, véhicules", regulation: "Régulation des dossiers, rendez-vous, établissements, accès patients (selon permissions)", admin: "Tous les droits : paramètres, utilisateurs, abonnement" };

// Changement de rôle : compte conservé (historique, points, dossiers), droits appliqués immédiatement côté serveur.
function RoleDialog({ u, onClose, onChanged }) {
  const [role, setRole] = useState(u.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    setBusy(true); setError("");
    try { await api.patch(`/users/${u.id}`, { role }); }
    catch (e) { setError(apiError(e.response?.data?.detail) || e.message); setBusy(false); return; }
    toast.success(`${u.first_name} est désormais ${ROLE_LABEL[role]} — droits appliqués immédiatement`);
    onChanged();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="role-dialog" className="max-w-md w-[calc(100vw-24px)] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Shield className="w-5 h-5 text-accent-700" />Modifier le rôle</DialogTitle>
          <DialogDescription>{u.first_name} {u.last_name} · {u.email}. Le compte, l'historique, les points, les documents et les rendez-vous sont conservés.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" role="radiogroup">
          {ROLES.map((r) => (
            <button key={r} type="button" role="radio" aria-checked={role === r} data-testid={`role-option-${r}`} onClick={() => setRole(r)}
              className={`w-full text-left rounded-xl border-2 p-3 transition-colors ${role === r ? "border-brand-800 bg-brand-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <p className="font-bold text-slate-900 text-sm">{ROLE_LABEL[r]}{u.role === r && <span className="ml-2 text-xs font-semibold text-slate-400">(actuel)</span>}</p>
              <p className="text-xs text-slate-500 mt-0.5">{ROLE_HINT[r]}</p>
            </button>
          ))}
        </div>
        {error && <p data-testid="role-error" className="text-sm text-red-600 font-semibold">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="h-11">Annuler</Button>
          <Button data-testid="role-save" onClick={save} disabled={busy || role === u.role} className="h-11 bg-brand-800 hover:bg-brand-900 text-white">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MENU_W = 240;

// Menu rendu dans document.body (portal) ancré au bouton ⋯ : jamais masqué par overflow/stacking du tableau ; s'ouvre vers le haut si manque de place.
function AnchoredMenu({ anchorRef, onClose, testid, children }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const h = menuRef.current?.offsetHeight || 160;
      const below = window.innerHeight - r.bottom - 8;
      const up = below < h && r.top > below;
      const left = Math.max(8, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8));
      setPos({ left, top: up ? r.top - h - 4 : r.bottom + 4 });
    };
    place();
    window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [anchorRef]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    const onDown = (e) => { if (!menuRef.current?.contains(e.target) && !anchorRef.current?.contains(e.target)) onClose(); };
    document.addEventListener("keydown", onKey); document.addEventListener("pointerdown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("pointerdown", onDown); };
  }, [onClose, anchorRef]);
  return createPortal(
    <div ref={menuRef} data-testid={testid} role="menu" style={{ position: "fixed", width: MENU_W, left: pos?.left ?? -9999, top: pos?.top ?? -9999, zIndex: 60 }}
      className="bg-white rounded-xl border border-slate-200 shadow-xl p-1.5 animate-fade-in text-left">
      {children}
    </div>, document.body);
}

// Menu d'actions par compte (admin) : invitation en attente → Renvoyer / Annuler ; compte actif → Désactiver / Réactiver / Supprimer (sans activité).
export function UserActions({ u, me, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const btnRef = useRef(null);
  const run = async (fn, ok) => {
    setBusy(true); setOpen(false);
    try { await fn(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); setBusy(false); return; }
    toast.success(ok);
    setBusy(false);
    onChanged(); // la ligne peut disparaître : le rechargement est géré (et protégé) par le parent
  };
  const invited = u.status === "invited";
  const self = u.id === me.id;
  return (
    <div className="relative inline-block text-left">
      <button ref={btnRef} data-testid={`user-actions-${u.id}`} aria-label="Actions" aria-expanded={open} disabled={busy} onClick={() => setOpen((o) => !o)} className="w-9 h-9 rounded-lg border border-slate-200 bg-white inline-flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
      </button>
      {open && (
        <AnchoredMenu anchorRef={btnRef} onClose={() => setOpen(false)} testid={`user-menu-${u.id}`}>
            {invited ? (
              <>
                <Item testid={`resend-invite-${u.id}`} icon={Send} onClick={() => run(() => api.post(`/users/${u.id}/resend-invitation`), `Invitation renvoyée à ${u.email}`)}>Renvoyer l'invitation</Item>
                <Item testid={`cancel-invite-${u.id}`} icon={XCircle} danger onClick={() => run(() => api.delete(`/users/${u.id}/invitation`), "Invitation annulée, adresse email libérée")}>Annuler l'invitation</Item>
              </>
            ) : (
              <>
                {!self && <Item testid={`change-role-${u.id}`} icon={Shield} onClick={() => { setOpen(false); setRoleOpen(true); }}>Modifier le rôle</Item>}
                {!self && (u.active
                  ? <Item testid={`deactivate-user-${u.id}`} icon={UserX} onClick={() => run(() => api.patch(`/users/${u.id}`, { active: false }), "Utilisateur désactivé (historique et points conservés)")}>Désactiver l'utilisateur</Item>
                  : <Item testid={`reactivate-user-${u.id}`} icon={UserCheck} onClick={() => run(() => api.patch(`/users/${u.id}`, { active: true }), "Utilisateur réactivé")}>Réactiver l'utilisateur</Item>)}
                {!self && <Item testid={`delete-user-${u.id}`} icon={Trash2} danger onClick={() => { setOpen(false); setDel(true); }}>Supprimer définitivement</Item>}
                {self && <p className="px-3 py-2 text-xs text-slate-400">Votre propre compte ne peut pas être modifié ici.</p>}
              </>
            )}
        </AnchoredMenu>
      )}
      {del && <DeleteUserDialog u={u} onClose={() => setDel(false)} onDeleted={() => { setDel(false); onChanged(); }} />}
      {roleOpen && <RoleDialog u={u} onClose={() => setRoleOpen(false)} onChanged={() => { setRoleOpen(false); onChanged(); }} />}
    </div>
  );
}

function Item({ icon: Icon, danger, testid, onClick, children }) {
  return <button data-testid={testid} onClick={onClick} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-50 ${danger ? "text-red-700 hover:bg-red-50" : "text-slate-700"}`}><Icon className="w-4 h-4" />{children}</button>;
}

function DeleteUserDialog({ u, onClose, onDeleted }) {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api.get(`/users/${u.id}/deletion-check`).then((r) => setCheck(r.data)).catch((e) => setError(apiError(e.response?.data?.detail) || e.message)); }, [u.id]);
  const rows = check ? Object.values(check.activity).filter((a) => a.count > 0) : [];
  const run = async () => {
    setBusy(true); setError("");
    try { await api.delete(`/users/${u.id}`); }
    catch (e) { setError(apiError(e.response?.data?.detail) || e.message); setBusy(false); return; }
    toast.success(`Compte ${u.email} supprimé, adresse libérée`);
    onDeleted();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="delete-user-dialog" className="max-w-md w-[calc(100vw-24px)] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-700 flex items-center gap-2"><Trash2 className="w-5 h-5" />Supprimer définitivement</DialogTitle>
          <DialogDescription>Réservé aux comptes de test sans activité. Un compte ayant des dossiers, un historique ou des points doit être désactivé pour préserver la traçabilité.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="font-semibold text-slate-800">{u.first_name} {u.last_name} <span className="text-slate-400 font-normal">· {u.email}</span></p>
          {!check && !error && <p className="text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Vérification de l'activité…</p>}
          {check?.deletable && <p data-testid="delete-user-ok" className="rounded-lg bg-green-50 border border-green-200 text-green-800 p-2.5 text-xs font-semibold">Aucune donnée métier liée : suppression possible. L'adresse email sera immédiatement réutilisable.</p>}
          {check && !check.deletable && (
            <div data-testid="delete-user-blocked" className="rounded-lg bg-orange-50 border border-orange-200 text-orange-800 p-2.5 text-xs">
              {check.blockers.map((b, i) => <p key={i} className="font-semibold flex gap-1.5"><Lock className="w-4 h-4 shrink-0" />{b}</p>)}
              {rows.length > 0 && <ul className="mt-2 grid grid-cols-2 gap-x-3">{rows.map((a) => <li key={a.label} className="flex justify-between"><span>{a.label}</span><b>{a.count}</b></li>)}</ul>}
            </div>
          )}
          {error && <p data-testid="delete-user-error" className="text-red-600">{error}</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="h-11">Annuler</Button>
          <Button data-testid="delete-user-confirm" onClick={run} disabled={!check?.deletable || busy} className="h-11 bg-red-600 hover:bg-red-700 text-white">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Supprimer définitivement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
